import {
	Box, Button,
	Flex,
	FormControl, FormLabel,
	Icon, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel, getFilteredRowModel,
	getSortedRowModel,
	useReactTable
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import { useEffect, useState } from "react";
import { MdOutlineDeleteOutline, MdOutlineEdit } from 'react-icons/md';
import { toast } from "react-toastify";
import * as VehicleService from 'services/VehicleService';
import { Vehicle } from 'utils/interfaces';

const columnHelper = createColumnHelper<Vehicle>();

export default function VehiclesConfigurations() {

	const { keycloak } = useKeycloak();

	const [vehicles, setVehicles] = useState<Vehicle[]>([]);
	const [vehicleName, setVehicleName] = useState('');
	const [vehiclePlate, setVehiclePlate] = useState('');
	const [editId, setEditId] = useState<number | null>(null);
	const [editName, setEditName] = useState('');
	const [editPlate, setEditPlate] = useState('');

	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [loading, setLoading] = useState(false);

	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(10);

	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const data = vehicles?.slice(startIndex, endIndex);
	const totalPages = vehicles?.length === 0 ? 1 : Math.ceil(vehicles.length / itemsPerPage);

	const textColor = useColorModeValue('secondaryGray.900', 'white');

	const onSessionExpired = () => {
		toast.error("Sessione scaduta. Effettua nuovamente l'accesso.", {
			position: "bottom-right",
			onClose: () => {
				keycloak.logout();
				localStorage.removeItem("kc-token");
			}
		});
	};

	const fetchVehicles = async () => {
		const response = await VehicleService.getAll(keycloak.token, onSessionExpired);
		setVehicles(response);
	};

	useEffect(() => {
		fetchVehicles();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const columns = [
		columnHelper.accessor('id', {
			id: 'id',
			header: () => (
				<Text marginLeft='auto' marginRight='auto' justifyContent='center' align='center'
					fontSize={{ sm: '10px', lg: '14px' }} textTransform="none" color='gray.400'>
					#
				</Text>
			),
			cell: (info: any) => (
				<Flex align='center'>
					<Text color={textColor} fontSize='xs' fontWeight='700' marginLeft='auto' marginRight='auto'>
						{startIndex + info.row.index + 1}
					</Text>
				</Flex>
			)
		}),
		columnHelper.accessor('name', {
			id: 'name',
			header: () => (
				<Text marginLeft='auto' marginRight='auto' justifyContent='space-between' align='center'
					fontSize={{ sm: '10px', lg: '14px' }} textTransform="none" color='gray.400'>
					Nome
				</Text>
			),
			cell: (info: any) => (
				<Flex align='center'>
					<Text color={textColor} fontSize='xs' fontWeight='700' marginLeft='auto' marginRight='auto'>
						{info.getValue()}
					</Text>
				</Flex>
			)
		}),
		columnHelper.accessor('plate', {
			id: 'plate',
			header: () => (
				<Text marginLeft='auto' marginRight='auto' justifyContent='space-between' align='center'
					fontSize={{ sm: '10px', lg: '14px' }} textTransform="none" color='gray.400'>
					Targa
				</Text>
			),
			cell: (info: any) => (
				<Flex align='center'>
					<Text color={textColor} fontSize='xs' fontWeight='700' marginLeft='auto' marginRight='auto'>
						{info.getValue() || '-'}
					</Text>
				</Flex>
			)
		}),
		{
			id: 'operation',
			header: () => (
				<Text marginLeft='auto' marginRight='auto' justifyContent='center' align='center'
					fontSize={{ sm: '10px', lg: '14px' }} textTransform="none" color='gray.400'>
					Azione
				</Text>
			),
			cell: (info: any) => (
				<Flex justifyContent='center'>
					<Button
						onClick={() => openEditModal(info.row.original.id, info.row.original.name, info.row.original.plate)}
						_hover={{ bg: 'gray.200' }} _active={{ bg: 'gray.100' }} _focus={{ boxShadow: 'none' }}
						color="white"
						style={{ height: '30px', padding: '0px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
						fontSize='xs' mx="1"
					>
						<Icon as={MdOutlineEdit} w={4} h={4} color="black" />
					</Button>
					<Button
						onClick={() => deleteVehicle(info.row.original.id)}
						_hover={{ bg: 'gray.200' }} _active={{ bg: 'gray.100' }} _focus={{ boxShadow: 'none' }}
						color="white"
						style={{ height: '30px', padding: '0px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
						fontSize='xs' mx="1"
					>
						<Icon as={MdOutlineDeleteOutline} w={4} h={4} color="black" />
					</Button>
				</Flex>
			)
		}
	];

	const table = useReactTable({
		columns,
		data,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		debugTable: false,
		debugHeaders: false,
		debugColumns: false,
	});

	const nextPage = () => setCurrentPage(p => Math.min(p + 1, totalPages));
	const prevPage = () => setCurrentPage(p => Math.max(p - 1, 1));

	const openEditModal = (id: number, name: string, plate: string | null) => {
		setEditId(id);
		setEditName(name);
		setEditPlate(plate || '');
	};

	const closeEditModal = () => {
		setEditId(null);
		setEditName('');
		setEditPlate('');
	};

	const openAddModal = () => setIsAddModalOpen(true);

	const closeAddModal = () => {
		setVehicleName('');
		setVehiclePlate('');
		setIsAddModalOpen(false);
	};

	const addVehicle = async () => {
		if (!vehicleName.trim()) return;
		setLoading(true);
		const response = await VehicleService.create(keycloak.token, onSessionExpired, { name: vehicleName, plate: vehiclePlate.trim() || undefined });
		if (response) {
			toast.success("Veicolo aggiunto con successo", { position: "bottom-right" });
			await fetchVehicles();
		} else {
			toast.error("Errore durante la creazione del veicolo", { position: "bottom-right" });
		}
		closeAddModal();
		setLoading(false);
	};

	const editVehicle = async () => {
		if (!editId || !editName.trim()) return;
		setLoading(true);
		const response = await VehicleService.update(keycloak.token, onSessionExpired, { id: editId, name: editName, plate: editPlate.trim() || undefined });
		if (response) {
			toast.success("Veicolo modificato con successo", { position: "bottom-right" });
			await fetchVehicles();
		} else {
			toast.error("Errore durante la modifica del veicolo", { position: "bottom-right" });
		}
		closeEditModal();
		setLoading(false);
	};

	const deleteVehicle = async (id: number) => {
		setLoading(true);
		const success = await VehicleService.deleteVehicle(keycloak.token, onSessionExpired, id);
		if (success) {
			toast.success("Veicolo eliminato con successo", { position: "bottom-right" });
			await fetchVehicles();
		} else {
			toast.error("Errore durante l'eliminazione del veicolo", { position: "bottom-right" });
		}
		setLoading(false);
	};

	return (
		<Box px='20px' mb="0px">
			{loading ? (
				<Flex justifyContent='center' alignItems='center' height='100vh'>
					<Spinner size='xl' />
				</Flex>
			) : (
				<Card>
					<Flex justifyContent='space-between' alignItems='center' mb={4}>
						<Text fontSize='xl' fontWeight='bold'>Veicoli Aziendali</Text>
						<Button
							onClick={openAddModal}
							_hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
							backgroundColor='#688CC8' color="white"
							style={{ height: '30px', padding: '0px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', marginLeft: 'auto' }}
							fontSize='xs' mx="1"
						>
							+ Aggiungi
						</Button>
					</Flex>
					<Table>
						<Thead>
							{table.getHeaderGroups().map(headerGroup => (
								<Tr key={headerGroup.id} p={1}>
									{headerGroup.headers.map(header => (
										<Th key={header.id} p={1}>
											{flexRender(header.column.columnDef.header, header.getContext())}
										</Th>
									))}
								</Tr>
							))}
						</Thead>
						<Tbody>
							{table.getRowModel().rows.map(row => (
								<Tr key={row.id} p={1}>
									{row.getVisibleCells().map(cell => (
										<Td key={cell.id} p={0.5}>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</Td>
									))}
								</Tr>
							))}
						</Tbody>
					</Table>
					{vehicles.length === 0 && (
						<Text textAlign="center" color="gray.500" py={4}>Nessun veicolo aziendale</Text>
					)}
					<Flex justifyContent='center' alignItems='center' mt={4}>
						<Button fontSize={{ base: 'xs', md: 'md' }} onClick={prevPage} isDisabled={currentPage === 1}>
							Precedente
						</Button>
						<Text fontSize={{ base: 'xs', md: 'md' }}>{`Pagina ${currentPage} di ${totalPages}`}</Text>
						<Button fontSize={{ base: 'xs', md: 'md' }} onClick={nextPage} isDisabled={currentPage === totalPages}>
							Successiva
						</Button>
					</Flex>
				</Card>
			)}

			{/* Edit modal */}
			<Modal isOpen={editId !== null} onClose={closeEditModal}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader color="#9FB50C">Modifica Veicolo</ModalHeader>
					<ModalCloseButton _focus={{ boxShadow: 'none' }} />
					<ModalBody>
						<FormControl mb={3}>
							<FormLabel>Nome <Text as="span" color="red.500">*</Text></FormLabel>
							<Input
								_focus={{ boxShadow: 'black' }}
								placeholder="Nome veicolo..."
								value={editName}
								onChange={(e) => setEditName(e.target.value)}
							/>
						</FormControl>
						<FormControl>
							<FormLabel>Targa</FormLabel>
							<Input
								_focus={{ boxShadow: 'black' }}
								placeholder="Targa..."
								value={editPlate}
								onChange={(e) => setEditPlate(e.target.value)}
							/>
						</FormControl>
					</ModalBody>
					<ModalFooter>
						<Button color="white" background="#688CC8" mr={3} onClick={editVehicle} isDisabled={!editName.trim()}>
							Salva
						</Button>
						<Button colorScheme="gray" onClick={closeEditModal}>Annulla</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Add modal */}
			<Modal isOpen={isAddModalOpen} onClose={closeAddModal}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader color="#9FB50C">Nuovo Veicolo</ModalHeader>
					<ModalCloseButton _focus={{ boxShadow: 'none' }} />
					<ModalBody>
						<FormControl mb={3}>
							<FormLabel>Nome <Text as="span" color="red.500">*</Text></FormLabel>
							<Input
								_focus={{ boxShadow: 'black' }}
								placeholder="Nome veicolo..."
								value={vehicleName}
								onChange={(e) => setVehicleName(e.target.value)}
							/>
						</FormControl>
						<FormControl>
							<FormLabel>Targa</FormLabel>
							<Input
								_focus={{ boxShadow: 'black' }}
								placeholder="Targa..."
								value={vehiclePlate}
								onChange={(e) => setVehiclePlate(e.target.value)}
							/>
						</FormControl>
					</ModalBody>
					<ModalFooter>
						<Button color="white" background="#688CC8" mr={3} onClick={addVehicle} isDisabled={!vehicleName.trim()}>
							Salva
						</Button>
						<Button colorScheme="gray" onClick={closeAddModal}>Annulla</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Box>
	);
}
